import { useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@core/config/runtimeConfig';

/** Visible, recently used tabs report presence. Server deduplicates parallel tabs. */
export function useUserPresence(token: string | null) {
  useEffect(() => {
    if (!token) return;
    let lastInput = Date.now();
    let pending = false;
    let failures = 0;
    let nextAttemptAt = 0;
    const controller = new AbortController();
    const input = () => { lastInput = Date.now(); };
    const beat = async () => {
      if (pending || Date.now() < nextAttemptAt || document.visibilityState !== 'visible' || !document.hasFocus() || Date.now() - lastInput > 300000) return;
      pending = true;
      try {
        await axios.post(`${API_BASE_URL.replace(/\/$/, '')}/v1/user/activity/heartbeat`, { active: true }, {
          headers: { Authorization: `Bearer ${token}` }, timeout: 10000, signal: controller.signal,
        });
        failures = 0;
        nextAttemptAt = Date.now() + 30000;
      } catch {
        // Back off during an outage rather than adding work to a saturated server.
        failures += 1;
        nextAttemptAt = Date.now() + Math.min(30000 * 2 ** Math.min(failures, 4), 300000);
      }
      finally { pending = false; }
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, input, { passive: true }));
    document.addEventListener('visibilitychange', beat);
    const interval = window.setInterval(beat, 30000);
    void beat();
    return () => {
      controller.abort(); window.clearInterval(interval);
      events.forEach(event => window.removeEventListener(event, input));
      document.removeEventListener('visibilitychange', beat);
    };
  }, [token]);
}
